import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    toast.error("This browser does not support push notifications.");
    return false;
  }
  try {
    // 1. permission
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      toast.error("Notifications permission denied.");
      return false;
    }
    // 2. get VAPID public key
    const { data } = await api.get("/push/vapid-public-key");
    const applicationServerKey = urlBase64ToUint8Array(data.key);
    // 3. subscribe with SW
    const reg = await navigator.serviceWorker.ready;
    // Clean up any stale subscription first
    const existing = await reg.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    const json = sub.toJSON();
    // 4. send to backend
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
    });
    toast.success("Browser notifications enabled");
    return true;
  } catch (e) {
    toast.error(formatApiError(e) || "Could not enable notifications");
    return false;
  }
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      try {
        await api.post("/push/unsubscribe", { endpoint });
      } catch (_) {}
    }
    toast.success("Browser notifications disabled");
    return true;
  } catch (e) {
    toast.error(formatApiError(e) || "Could not disable notifications");
    return false;
  }
}

export async function sendTestPush() {
  try {
    await api.post("/push/test", {});
    toast.success("Test notification sent");
  } catch (e) {
    toast.error(formatApiError(e) || "Failed to send test");
  }
}

export async function hasActiveSubscription() {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}
